import type { HekateDisplayField, HekateDisplayForm } from '@onshape/www-inertia';
import { L1Validations, Trans } from '@onshape/www-inertia';
import ExternalUtil from '../../../ExternalUtil';
import type { ButtonPortableText } from '~/sanity/types';

export default class FormFacetCoordinator {
  private readonly formDefinition: Ref<HekateDisplayForm>;

  private readonly formElement: Ref<null | HTMLFormElement>;

  private readonly isShowThankYou: Ref<boolean>;

  private readonly isFormSubmitting: Ref<boolean>;

  private readonly supportEmailText: Ref<string>;

  private readonly successMessagePortableText: Ref<ButtonPortableText | undefined>;

  private readonly isShowSuccessMessage: Ref<boolean>;

  private readonly isShow409Error: Ref<boolean>;

  private readonly isShowForm: Ref<boolean>;

  private readonly onSubmitForm: (_evt: Event) => void;

  constructor({
    formDefinition,
    formElement,
    supportEmailText,
    isFormSubmitting,
    isShowThankYou,
    onSubmitForm,
    isShowSuccessMessage,
    successMessagePortableText,
    isShow409Error,
    isShowForm,
  }: {
    formDefinition: Ref<HekateDisplayForm>;
    formElement: Ref<null | HTMLFormElement>;
    supportEmailText: Ref<string>;
    isFormSubmitting: Ref<boolean>;
    isShowThankYou: Ref<boolean>;
    onSubmitForm: (_evt: Event) => void;
    isShowSuccessMessage: Ref<boolean>;
    successMessagePortableText: Ref<ButtonPortableText | undefined>;
    isShow409Error: Ref<boolean>;
    isShowForm: Ref<boolean>;
  }) {
    this.onSubmitForm = onSubmitForm;
    this.formDefinition = formDefinition;
    this.formElement = formElement;
    this.isShowThankYou = isShowThankYou;
    this.isFormSubmitting = isFormSubmitting;
    this.supportEmailText = supportEmailText;
    this.successMessagePortableText = successMessagePortableText;
    this.isShowSuccessMessage = isShowSuccessMessage;
    this.isShow409Error = isShow409Error;
    this.isShowForm = isShowForm;
  }

  /** Validate the field on blur */
  private static onFieldBlur(field: HekateDisplayField) {
    const persistErrors = field.errorMessages.filter((err) => err.isPersisted);
    L1Validations.blurSingleField({ field }).then((fieldError) => {
      field.errorMessages.splice(0, field.errorMessages.length);
      field.isValidated = false;
      if (fieldError) {
        field.errorMessages.push({
          message: fieldError.message,
          hint: fieldError.hint,
        });
        return;
      }
      if (persistErrors.length) {
        field.errorMessages.push(...persistErrors);
        return;
      }
      field.isValidated = field.val !== '';
    });
  }

  /** Clear/Reset validation on focus */
  private static onFieldFocus(field: HekateDisplayField) {
    field.isValidated = false;
  }

  private clear409Error() {
    this.isShow409Error.value = false;
    ExternalUtil.safeResetRecaptcha();
  }

  public setup() {
    if (window.wwwHekateTranslations) {
      Trans.populateTranslations({
        language: ExternalUtil.getSiteLang(),
        translations: window.wwwHekateTranslations,
      });
    } else {
      // TODO: THIS SHOULD POPULATE TRANSLATIONS ON SERVER SIDE RENDERING, NOT CLIENT SIDE
      Trans.populateTranslations({
        language: ExternalUtil.getSiteLang(),
        translations: [],
      });
    }

    return {
      formElement: this.formElement,
      formDefinition: this.formDefinition,
      isShowThankYou: this.isShowThankYou,
      isFormSubmitting: this.isFormSubmitting,
      supportEmailText: this.supportEmailText,
      successMessagePortableText: this.successMessagePortableText,
      isShowSuccessMessage: this.isShowSuccessMessage,
      isShow409Error: this.isShow409Error,
      isShowForm: this.isShowForm,
      onFieldBlur: FormFacetCoordinator.onFieldBlur.bind(this),
      onFieldFocus: FormFacetCoordinator.onFieldFocus,
      onSubmitForm: this.onSubmitForm,
      clear409Error: this.clear409Error.bind(this),
    };
  }
}
